import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { debounce, isEqual, pick } from 'lodash';

import { marketValues, portfolios, securities } from '../api';
import { selectors } from '../store';
import {
  getPeriodFromDate,
  lastReconciledDate,
  lastReconciledMonthRange,
  transformActiveFilters,
  Sentry,
} from '../services';

const {
  ui: {
    portfolio: { activeFiltersSelector },
  },
} = selectors;

const PORTFOLIO_SELECTION_FILTERS = [
  'ssids',
  'account',
  'taxClass',
  'showInternalPortfolio',
  'showExternalPortfolio',
];

const EMPTY_DATA = {
  month: { portfolios: [], marketValues: [], securities: [] },
  yearToDate: { marketValues: [] },
  lastTwelveMonths: { marketValues: [] },
};

const EMPTY_ERRORS = {
  month: { portfolios: null, marketValues: null, securities: null },
  yearToDate: { marketValues: null },
  lastTwelveMonths: { marketValues: null },
};

const toApiDate = (date) =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    .toISOString()
    .split('T')[0];

const mapResponseData = (response) => {
  if (!Array.isArray(response?.data)) {
    throw new Error('Expected a JSON:API response with a data array');
  }

  return response.data.map((item) => ({
    id: item.id,
    type: item.type,
    ...item.attributes,
  }));
};

const requestData = async (name, args, request) => {
  try {
    const response = await request();
    return mapResponseData(response);
  } catch (error) {
    console.error(`[StoryMode] ${name} failed`, {
      status: error?.status ?? error?.response?.status,
      statusText: error?.response?.statusText,
      message: error?.message,
      startDate: args.startDate,
      endDate: args.endDate,
    });
    throw error;
  }
};

const settledValue = (result) =>
  result.status === 'fulfilled' ? result.value : [];

const settledError = (result) =>
  result.status === 'rejected' ? result.reason : null;

// TWELVEMONTHS is anchored to today, so its last row is the current, still
// unreconciled month. The story looks back at a closed month, so the series is
// cut there — leaving the month the story is about as the last data point.
const untilLastReconciledMonth = (items) => {
  const lastReconciledPeriod = Number(getPeriodFromDate(lastReconciledDate()));
  return items.filter((item) => Number(item.p) <= lastReconciledPeriod);
};

/**
 * Fetches Story Mode data without putting it in the entity store. The entity
 * store is assumed to match the active portfolio timeframe, while Story Mode
 * always uses the last reconciled month.
 *
 * This follows the detached-fetch pattern used by web's PeriodicChange: use
 * the existing API modules, transform the active portfolio filters, and map
 * the JSON:API response directly into the shape consumed by entity selectors.
 */
export const useStoryModeData = () => {
  const activeFilters = useSelector(activeFiltersSelector);
  const initialized = useSelector((state) => state.ui.portfolio.initialized);
  const debounceTime = useSelector((state) => state.ui.portfolio.debounceTime);

  const [data, setData] = useState(EMPTY_DATA);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [isLoading, setIsLoading] = useState(true);

  const previousSelection = useRef(null);
  const retryTimeout = useRef(null);

  const fetchData = useRef(
    debounce(async function run(filters, isRetry = false) {
      setIsLoading(true);
      setErrors(EMPTY_ERRORS);

      // Keeps the spinner up across a scheduled retry instead of flashing an
      // empty story for a second.
      let retrying = false;

      try {
        const { startDate, endDate } = lastReconciledMonthRange();
        const monthArgs = transformActiveFilters({
          ...filters,
          startDate: toApiDate(startDate),
          endDate: toApiDate(endDate),
        });
        const yearToDateArgs = transformActiveFilters({
          ...filters,
          startDate: toApiDate(new Date(endDate.getFullYear(), 0, 1)),
          endDate: toApiDate(endDate),
        });
        // Its own request rather than a slice of year-to-date: `twr` is
        // cumulative from the requested start date, so a wider range cannot be
        // narrowed after the fact without re-chaining the returns.
        // transformActiveFilters only forwards `period` when no explicit dates
        // are set, so the inherited ones are cleared.
        const lastTwelveMonthsArgs = transformActiveFilters({
          ...filters,
          period: 'TWELVEMONTHS',
          startDate: null,
          endDate: null,
        });

        const results = await Promise.allSettled([
          requestData('portfolios (month)', monthArgs, () =>
            portfolios.get(monthArgs).then(([response]) => response)
          ),
          requestData('market values (month)', monthArgs, () =>
            marketValues.get(monthArgs)
          ),
          requestData('portfolio lenses (month)', monthArgs, () =>
            securities.get(monthArgs)
          ),
          requestData('market values (year to date)', yearToDateArgs, () =>
            marketValues.get(yearToDateArgs)
          ),
          requestData(
            'market values (last twelve months)',
            lastTwelveMonthsArgs,
            () => marketValues.get(lastTwelveMonthsArgs)
          ),
        ]);

        // Promise.allSettled never rejects, so a failed request shows up here
        // rather than in the catch below. Retry the batch once before showing a
        // half-empty story; only report if the retry fails too.
        const failed = results.filter((result) => result.status === 'rejected');
        if (failed.length && !isRetry) {
          retrying = true;
          retryTimeout.current = setTimeout(() => run(filters, true), 1000);
          return;
        }
        failed.forEach((result) =>
          Sentry.captureException(result.reason, {
            tags: { feature: 'storyMode' },
          })
        );

        setData({
          month: {
            portfolios: settledValue(results[0]),
            marketValues: settledValue(results[1]),
            securities: settledValue(results[2]),
          },
          yearToDate: {
            marketValues: settledValue(results[3]),
          },
          lastTwelveMonths: {
            marketValues: untilLastReconciledMonth(settledValue(results[4])),
          },
        });
        setErrors({
          month: {
            portfolios: settledError(results[0]),
            marketValues: settledError(results[1]),
            securities: settledError(results[2]),
          },
          yearToDate: {
            marketValues: settledError(results[3]),
          },
          lastTwelveMonths: {
            marketValues: settledError(results[4]),
          },
        });
      } catch (error) {
        console.error('[StoryMode] Failed to prepare data requests', error);
        Sentry.captureException(error, { tags: { feature: 'storyMode' } });
      } finally {
        if (!retrying) setIsLoading(false);
      }
    }, debounceTime)
  ).current;

  useEffect(() => {
    return () => {
      fetchData.cancel();
      clearTimeout(retryTimeout.current);
    };
  }, [fetchData]);

  useEffect(() => {
    if (!initialized || !activeFilters?.ssids?.length) return;

    const selection = pick(activeFilters, PORTFOLIO_SELECTION_FILTERS);
    if (isEqual(selection, previousSelection.current)) return;

    previousSelection.current = selection;
    fetchData(activeFilters);
  }, [initialized, activeFilters, fetchData]);

  // Stable identity: this is handed straight to a context provider, so a new
  // object on every render re-renders every slide in the story.
  return useMemo(
    () => ({ ...data, errors, isLoading }),
    [data, errors, isLoading]
  );
};
